/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { WaypointType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface WaypointSummaryProps {
  id: string;
  name: string;
  abbreviation: string;
  type: WaypointType;
}

export class WaypointSummaryRecord extends Record<WaypointSummaryProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  type: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: WaypointType;
  }): WaypointSummaryRecord {
    if (json == null) {
      throw new Error('WaypointSummaryRecord.fromJS: parameter cannot be null');
    }
    return new WaypointSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'WaypointSummaryJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'WaypointSummaryJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'WaypointSummaryJson.abbreviation'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', WaypointType.Post),
        'WaypointSummaryJson.type',
      ),
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: WaypointType;
  }): [WaypointSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('WaypointSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new WaypointSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'WaypointSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'WaypointSummaryJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'WaypointSummaryJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', WaypointType.Post),
            'WaypointSummaryJson.type',
          ),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: WaypointSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('WaypointSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class WaypointSummaryViewModelBase implements WaypointSummaryProps {
  protected readonly _props: WaypointSummaryProps;
  private _json?: WaypointSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get type(): WaypointType {
    return this._props.type;
  }

  public get isDropoffTeam() {
    return this._props.type === WaypointType.DropoffTeam;
  }
  public get isEnRouteDiversion() {
    return this._props.type === WaypointType.EnRouteDiversion;
  }
  public get isFuelBurn() {
    return this._props.type === WaypointType.FuelBurn;
  }
  public get isFuelLocation() {
    return this._props.type === WaypointType.FuelLocation;
  }
  public get isOther() {
    return this._props.type === WaypointType.Other;
  }
  public get isPatientDropoffRendezvous() {
    return this._props.type === WaypointType.PatientDropoffRendezvous;
  }
  public get isPatientPickupRendezvous() {
    return this._props.type === WaypointType.PatientPickupRendezvous;
  }
  public get isPickupTeam() {
    return this._props.type === WaypointType.PickupTeam;
  }
  public get isPost() {
    return this._props.type === WaypointType.Post;
  }
  public get isPublicRelationsSite() {
    return this._props.type === WaypointType.PublicRelationsSite;
  }
  public get isReceivingFacility() {
    return this._props.type === WaypointType.ReceivingFacility;
  }
  public get isRendezvousPoint() {
    return this._props.type === WaypointType.RendezvousPoint;
  }
  public get isSceneLocation() {
    return this._props.type === WaypointType.SceneLocation;
  }
  public get isSendingFacility() {
    return this._props.type === WaypointType.SendingFacility;
  }

  public isAnyType(...waypointType: WaypointType[]) {
    return waypointType.includes(this._props.type);
  }

  constructor(props: WaypointSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        type: this._props.type,
      };
    }
    return this._json;
  }
}
